/**
 * Puente Escuchar → Conversación (Fase 19.D, Workstream E).
 *
 * El player abre la conversación EXISTENTE pasando el capítulo escuchado por
 * `navigate('/conversation', { state: { listen } })`. El controller de
 * conversación lee ese contexto, lo inyecta como `moduleHints.listen` (JSON
 * string — el wsServer ya lo parsea, 19.C) y, al cerrar, marca "conversado" y
 * vuelve al player. Sin pantalla nueva de conversación: es un foco más, como
 * Noticias/Escenarios.
 */

import type { Cefr, Edition } from './types.js';

/**
 * Modo de conversación sobre el capítulo (19.G):
 *  - 'chat'   — charla libre sobre lo escuchado (histórico).
 *  - 'retell' — el user lo cuenta con sus palabras; el coach escucha con
 *               backchannel y recién al final contrasta contra el transcript.
 *  - 'debate' — el coach toma la postura contraria y el user defiende la idea.
 */
export type ListenMode = 'chat' | 'retell' | 'debate';

/** Contexto que viaja por `location.state.listen` al abrir la conversación. */
export interface ListenStartContext {
  book: string;
  edition: Edition;
  keypoint: string;
  cefr: Cefr;
  lang: string;
  mode: ListenMode;
}

const EDITIONS: readonly Edition[] = ['short', 'long'];
const CEFRS: readonly Cefr[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const MODES: readonly ListenMode[] = ['chat', 'retell', 'debate'];

/**
 * Valida defensivamente el `location.state.listen` (viene del router, no se
 * confía a ciegas). Devuelve el contexto tipado o null si está ausente/mal
 * formado (en cuyo caso la conversación arranca como charla libre normal).
 */
export function parseListenStart(state: unknown): ListenStartContext | null {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { listen?: unknown }).listen;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (
    typeof o.book !== 'string' ||
    typeof o.keypoint !== 'string' ||
    typeof o.lang !== 'string' ||
    !EDITIONS.includes(o.edition as Edition) ||
    !CEFRS.includes(o.cefr as Cefr)
  ) {
    return null;
  }
  return {
    book: o.book,
    edition: o.edition as Edition,
    keypoint: o.keypoint,
    cefr: o.cefr as Cefr,
    lang: o.lang,
    // Modo opcional (19.G): ausente o desconocido → charla libre histórica.
    mode: MODES.includes(o.mode as ListenMode) ? (o.mode as ListenMode) : 'chat',
  };
}
